import React, { useCallback, useReducer } from 'react';
import gql from 'graphql-tag';
import classNames from 'classnames';
import { Button, TextInput } from '@moda/om';
import { Link } from '../Link';
import { EmailPumoModuleFormFragment } from '../../generated/types/EmailPumoModuleFormFragment';
import { NormalizePumoStyles, sanitizeHTML } from '../NormalizePumoStyles';
import { reportError } from '../../lib/reportError';
import { setNewsletterPreference } from '../../lib/setNewsletterPreference';
import { Gender } from '../../constants/gender';
import './EmailPumoModuleForm.scss';

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  emailUnitForm: EmailPumoModuleFormFragment;
}

interface State {
  successful: boolean;
  error: string | null;
  user: {
    email: string | null;
    gender: Gender;
  };
}

export const EMAIL_PUMO_MODULE_FORM_FRAGMENT = gql`
  fragment EmailPumoModuleFormFragment on EmailUnit {
    ctaText
    useDivider
    genderType
    eyebrowText {
      body
    }
    headlineText {
      body
    }
    taglineText {
      body
    }
  }
`;

type Action =
  | { type: 'UPDATE_USER'; payload: { email?: string; gender?: Gender } }
  | { type: 'ERROR'; payload: { error: string } }
  | { type: 'SUCCESS' };

const reducer = (state: State, action: Action) => {
  switch (action.type) {
    case 'UPDATE_USER':
      return { ...state, user: { ...state.user, ...action.payload }, successful: false };
    case 'ERROR':
      return { ...state, error: action.payload.error };
    case 'SUCCESS':
      return { ...state, successful: true, error: null, user: { ...state.user, email: null } };
  }
};

export const EmailPumoModuleForm: React.FC<Props> = ({
  className,
  emailUnitForm: { eyebrowText, headlineText, taglineText, ctaText, useDivider }
}) => {
  const [state, dispatch] = useReducer(reducer, {
    successful: false,
    error: null,
    user: {
      email: null,
      gender: 'women'
    }
  });

  const onSuccess = useCallback(() => dispatch({ type: 'SUCCESS' }), []);
  const onFail = useCallback(err => {
    dispatch({
      type: 'ERROR',
      payload: { error: 'Unable to subscribe you at this time' }
    });
    reportError(err);
  }, []);

  const handleSubmit = useCallback(
    (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      setNewsletterPreference({
        user: state.user,
        onSuccess,
        onFail,
        placement: 'emailPumoModule'
      });
    },
    [onSuccess, onFail, state.user]
  );

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) =>
      dispatch({ type: 'UPDATE_USER', payload: { email: event.currentTarget.value } }),
    []
  );

  return (
    <div className={classNames('EmailPumoModuleForm', className)}>
      <div className="EmailPumoModuleForm__text">
        {eyebrowText?.body && (
          <div className="EmailPumoModuleForm__eyebrow">{eyebrowText.body}</div>
        )}

        {headlineText?.body && (
          <div className="EmailPumoModuleForm__headline">{headlineText.body}</div>
        )}

        {useDivider && <hr className="EmailPumoModuleForm__divider" />}

        {taglineText?.body && taglineText?.body !== '' && (
          <NormalizePumoStyles
            className="EmailPumoModuleForm__tagline"
            dangerouslySetInnerHTML={{ __html: `${sanitizeHTML(taglineText.body)}` }}
          />
        )}

        {ctaText && <span className="EmailPumoModuleForm__cta">{ctaText}</span>}
      </div>

      <form className="EmailPumoModuleForm__form" onSubmit={handleSubmit}>
        <TextInput
          required
          type="email"
          value={state.user.email || ''}
          placeholder={state.successful ? 'Email submitted!' : 'Sign up for our newsletter'}
          onChange={handleChange}
          error={!!state.error}
        />

        <Button type="submit">Submit</Button>

        <footer className="EmailPumoModuleForm__privacy-policy">
          To opt-out of e-mail sharing or to view our privacy policy,{' '}
          <Link to="/privacy">click here</Link>.
        </footer>
      </form>
    </div>
  );
};
